// Admin · Restock alerts (who is waiting for what) + review-request email log.
import { useCallback, useEffect, useState } from 'react';
import { API_BASE } from '../lib/apiBase';

const TOKEN_KEY = 'loadout.admin.community.token';
const readToken = () => { try { return window.localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; } };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const th = 'text-left font-mono text-[10px] uppercase tracking-[0.16em] text-white/45 py-2 pr-4';
const td = 'py-2.5 pr-4 border-t border-white/10 align-top';

function requestStatus(r) {
        if (r.sent_at) return `Sent ${fmt(r.sent_at)}`;
        if (r.skipped_reason) return `Skipped (${r.skipped_reason.replace(/_/g, ' ')})`;
        return 'Scheduled';
}

export default function AdminRestockAlerts() {
        const [token] = useState(readToken);
        const [alerts, setAlerts] = useState(null);
        const [requests, setRequests] = useState([]);
        const [denied, setDenied] = useState(!token);

        const load = useCallback(async () => {
                const h = { Authorization: `Bearer ${token}` };
                const [a, r] = await Promise.all([
                        fetch(`${API_BASE}/api/admin/restock-alerts`, { headers: h }).catch(() => null),
                        fetch(`${API_BASE}/api/admin/review-requests`, { headers: h }).catch(() => null),
                ]);
                if (a?.status === 401) { setDenied(true); return; }
                if (a?.ok) setAlerts((await a.json()).alerts || []);
                if (r?.ok) setRequests((await r.json()).requests || []);
        }, [token]);
        useEffect(() => { if (token) load(); }, [token, load]);

        if (denied) {
                return (
                        <main data-testid="admin-restock-login-needed" className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6">
                                <p className="font-mono text-[12px] uppercase tracking-[0.18em]">Sign in on <a className="text-[#FF9F1C] underline" href="/admin/reviews">/admin/reviews</a> first, then come back.</p>
                        </main>
                );
        }
        return (
                <main data-testid="admin-restock-page" className="min-h-screen bg-[#0A0A0A] text-white px-6 py-10">
                        <div className="mx-auto max-w-[1100px]">
                                <p className="font-mono text-[11px] tracking-[0.18em] text-[#FF9F1C]">LOADOUT / ADMIN</p>
                                <h1 className="mt-2 font-display text-[32px] uppercase">Restock alerts</h1>
                                <p className="mt-1 font-sans text-[13px] text-white/55">Shoppers waiting per variant. Emails go out automatically within 30 minutes of stock returning.</p>
                                <div className="mt-6 overflow-x-auto">
                                        {alerts && alerts.length === 0 ? (
                                                <p data-testid="admin-restock-empty" className="font-mono text-[12px] uppercase tracking-[0.18em] text-white/45">No sign-ups yet.</p>
                                        ) : (
                                                <table data-testid="admin-restock-table" className="w-full font-sans text-[13px]">
                                                        <thead><tr><th className={th}>Product</th><th className={th}>Variant</th><th className={th}>Waiting</th><th className={th}>Notified</th><th className={th}>Last sign-up</th></tr></thead>
                                                        <tbody>
                                                                {(alerts || []).map((a) => (
                                                                        <tr key={a.variant_id} data-testid="admin-restock-row">
                                                                                <td className={td}><a href={`/p/${a.product_handle}`} target="_blank" rel="noreferrer" className="hover:text-[#FF9F1C]">{a.product_title}</a></td>
                                                                                <td className={`${td} text-white/60`}>{a.variant_title === 'Default Title' ? '—' : a.variant_title}</td>
                                                                                <td className={`${td} font-mono text-[#FF9F1C]`}>{a.waiting}</td>
                                                                                <td className={`${td} font-mono text-white/60`}>{a.notified}</td>
                                                                                <td className={`${td} text-white/60`}>{fmt(a.last_signup)}</td>
                                                                        </tr>
                                                                ))}
                                                        </tbody>
                                                </table>
                                        )}
                                </div>

                                <h2 className="mt-14 font-display text-[22px] uppercase">Review request emails</h2>
                                <p className="mt-1 font-sans text-[13px] text-white/55">Orders recorded when Shopify marks them fulfilled. The email goes out 10 days later at 10:30am IST.</p>
                                <div className="mt-6 overflow-x-auto">
                                        {requests.length === 0 ? (
                                                <p data-testid="admin-review-requests-empty" className="font-mono text-[12px] uppercase tracking-[0.18em] text-white/45">No fulfilled orders recorded yet.</p>
                                        ) : (
                                                <table data-testid="admin-review-requests-table" className="w-full font-sans text-[13px]">
                                                        <thead><tr><th className={th}>Order</th><th className={th}>Email</th><th className={th}>Fulfilled</th><th className={th}>Status</th></tr></thead>
                                                        <tbody>
                                                                {requests.map((r) => (
                                                                        <tr key={r.order_id} data-testid="admin-review-request-row">
                                                                                <td className={`${td} font-mono`}>{r.order_name}</td>
                                                                                <td className={`${td} text-white/60`}>{r.email}</td>
                                                                                <td className={`${td} text-white/60`}>{fmt(r.fulfilled_at)}</td>
                                                                                <td className={td}>{requestStatus(r)}</td>
                                                                        </tr>
                                                                ))}
                                                        </tbody>
                                                </table>
                                        )}
                                </div>
                        </div>
                </main>
        );
}
